import FlowSubjectRow from "../features/calculator/components/FlowSubjectRow";
import FlowChoice from "../features/calculator/components/FlowChoice";
import FlowLevelSelect from "../features/calculator/components/FlowLevelSelect";
import useFlowDefaults from "../features/calculator/hooks/useFlowDefaults";
/* src/components/GroupThree.jsx
   IB Group 3 — Individuals and Societies */
import React, { useState, useEffect, useMemo } from "react";
import { useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useTZ } from "../contexts/TimezoneContext";
import { useGradeActions } from "../contexts/GradesContext";
import { getEntryForSubject } from "../utils/timezone";
import { getBoundaryResolutionOrder } from "../utils/boundaryCatalog";
import useBoundaryCatalog from "../hooks/useBoundaryCatalog";
import { getBoundarySubjectMeta } from "../data/subjectCatalog";
import { getPointsAboveBoundary } from "../features/subjects/utils/subjectScoring";

const COMPONENTS = {
  "Art History_SL": [["p1", "Paper 1", 20, "20%"], ["p2", "Paper 2", 48, "48%"], ["ia", "IA", 32, "32%"]],
  "Brazilian Social Studies_SL": [["p1", "Paper 1", 40, "35%"], ["p2", "Paper 2", 30, "40%"], ["ia", "IA", 28, "25%"]],
  "Business Management_SL": [["p1", "Paper 1", 30, "35%"], ["p2", "Paper 2", 40, "35%"], ["ia", "IA", 25, "30%"]],
  "Business Management_HL": [["p1", "Paper 1", 30, "25%"], ["p2", "Paper 2", 50, "30%"], ["p3", "Paper 3", 25, "25%"], ["ia", "IA", 25, "20%"]],
  "Economics_SL": [["p1", "Paper 1", 25, "30%"], ["p2", "Paper 2", 40, "40%"], ["ia", "Portfolio", 45, "30%"]],
  "Economics_HL": [["p1", "Paper 1", 25, "20%"], ["p2", "Paper 2", 40, "30%"], ["p3", "Paper 3", 60, "30%"], ["ia", "Portfolio", 45, "20%"]],
  "Environmental Systems and Societies_SL": [["p1", "Paper 1", 35, "25%"], ["p2", "Paper 2", 65, "50%"], ["ia", "IA", 30, "25%"]],
  "Geography_SL": [["p1", "Paper 1", 40, "35%"], ["p2", "Paper 2", 50, "40%"], ["ia", "Fieldwork", 25, "25%"]],
  "Geography_HL": [["p1", "Paper 1", 60, "35%"], ["p2", "Paper 2", 50, "25%"], ["p3", "Paper 3", 28, "20%"], ["ia", "Fieldwork", 25, "20%"]],
  "Global Politics_SL": [["p1", "Paper 1", 25, "30%"], ["p2", "Paper 2", 50, "45%"], ["ia", "Engagement Activity", 20, "25%"]],
  "Global Politics_HL": [["p1", "Paper 1", 25, "20%"], ["p2", "Paper 2", 75, "40%"], ["ia", "Engagement Activity", 20, "20%"], ["ext", "HL Ext.", 20, "20%"]],
  "History_SL": [["p1", "Paper 1", 24, "30%"], ["p2", "Paper 2", 30, "45%"], ["ia", "IA", 25, "25%"]],
  "History_Europe": [["p1", "Paper 1", 24, "20%"], ["p2", "Paper 2", 30, "25%"], ["p3", "Paper 3", 45, "35%"], ["ia", "IA", 25, "20%"]],
  "History_Americas": [["p1", "Paper 1", 24, "20%"], ["p2", "Paper 2", 30, "25%"], ["p3", "Paper 3", 45, "35%"], ["ia", "IA", 25, "20%"]],
  "History_Asia and Oceania": [["p1", "Paper 1", 24, "20%"], ["p2", "Paper 2", 30, "25%"], ["p3", "Paper 3", 45, "35%"], ["ia", "IA", 25, "20%"]],
  "History_Africa and Middle East": [["p1", "Paper 1", 24, "20%"], ["p2", "Paper 2", 30, "25%"], ["p3", "Paper 3", 45, "35%"], ["ia", "IA", 25, "20%"]],
  "Digital Society_SL": [["p1", "Paper 1", 40, "40%"], ["p2", "Paper 2", 24, "30%"], ["ia", "Inquiry", 24, "30%"]],
  "Digital Society_HL": [["p1", "Paper 1", 52, "35%"], ["p2", "Paper 2", 24, "20%"], ["p3", "Paper 3", 30, "25%"], ["ia", "Inquiry", 24, "20%"]],
  "Philosophy_SL": [["p1", "Paper 1", 50, "50%"], ["p2", "Paper 2", 25, "25%"], ["ia", "Phil. Analysis", 25, "25%"]],
  "Philosophy_HL": [["p1", "Paper 1", 75, "40%"], ["p2", "Paper 2", 25, "20%"], ["p3", "Paper 3", 25, "20%"], ["ia", "Phil. Analysis", 25, "20%"]],
  "Psychology_SL": [["p1", "Paper 1", 49, "50%"], ["p2", "Paper 2", 22, "25%"], ["ia", "Exp. Study", 22, "25%"]],
  "Psychology_HL": [["p1", "Paper 1", 49, "40%"], ["p2", "Paper 2", 44, "20%"], ["p3", "Paper 3", 24, "20%"], ["ia", "Exp. Study", 22, "20%"]],
  "Social and Cultural Anthropology_SL": [["p1", "Paper 1", 30, "40%"], ["p2", "Paper 2", 30, "40%"], ["ia", "Fieldwork", 26, "20%"]],
  "Social and Cultural Anthropology_HL": [["p1", "Paper 1", 40, "30%"], ["p2", "Paper 2", 45, "45%"], ["ia", "Fieldwork", 26, "25%"]],
  "Turkey in the 20th Century_SL": [["p1", "Paper 1", 24, "30%"], ["p2", "Paper 2", 30, "45%"], ["ia", "IA", 25, "25%"]],
  "World Religions_SL": [["p1", "Paper 1", 50, "30%"], ["p2", "Paper 2", 40, "45%"], ["ia", "IA", 30, "25%"]],
};

const normalizeSpaces = (value) => value.replace(/\s+/g, " ").trim();
const normalizeHistoryPathway = (value) =>
  normalizeSpaces(value).replace(/\bAnd\b/g, "and");

const parseGroupThreeSubjectKey = (key) => {
  const match = key.match(/^(SL|HL)\s+(.+)$/i);
  if (!match) return null;
  const level = match[1].toUpperCase();
  const subjectPart = normalizeSpaces(match[2]);
  if (/^History\s+/i.test(subjectPart)) {
    const rawPathway = subjectPart.replace(/^History\s+/i, "").trim();
    return {
      key,
      level,
      subject: "History",
      pathway: rawPathway ? normalizeHistoryPathway(rawPathway) : null,
    };
  }
  return { key, level, subject: subjectPart, pathway: null };
};

function GroupThree({
  groupId = "3",
  boundary = "m24",
  onBoundaryNotFound,
  headerExtras = null,
  afterTitleExtras = null,
}) {
  const { tz } = useTZ();
  const { updateGrade } = useGradeActions();
  const { dataByBoundary, catalogKeys, isLoading } = useBoundaryCatalog(boundary, {
    includeAllBoundaries: true,
  });

  const storagePath = `groups.${groupId}.groupThree`;
  useFlowDefaults(storagePath, "groupThree", !isLoading);
  const [subject, setSubject] = useCalculatorStoredValue(`${storagePath}.subject`, "");
  const [level, setLevel] = useCalculatorStoredValue(`${storagePath}.level`, "");
  const [pathway, setPathway] = useCalculatorStoredValue(`${storagePath}.pathway`, "");
  const [scores, setScores] = useCalculatorStoredValue(`${storagePath}.scores`, {});
  const [boundaryNotFound, setBoundaryNotFound] = useState(false);

  useEffect(() => { setBoundaryNotFound(false); }, [boundary]);

  const LEVELS = ["SL", "HL"];
  const parsedSubjects = catalogKeys
    .filter((key) => getBoundarySubjectMeta(key)?.groupId === "3")
    .map(parseGroupThreeSubjectKey)
    .filter(Boolean);

  const levelsBySubject = parsedSubjects.reduce((acc, item) => {
    if (!acc[item.subject]) acc[item.subject] = new Set();
    acc[item.subject].add(item.level);
    return acc;
  }, {});

  const subjectOptions = (
    level
      ? Object.keys(levelsBySubject).filter((s) => levelsBySubject[s]?.has(level))
      : Object.keys(levelsBySubject)
  ).sort((a, b) => a.localeCompare(b));

  const availableLevels = subject
    ? LEVELS.filter((l) => levelsBySubject[subject]?.has(l))
    : [];

  const historyPathwayOptions =
    subject === "History" && level === "HL"
      ? Array.from(
          new Set(
            parsedSubjects
              .filter((item) => item.subject === "History" && item.level === "HL" && item.pathway)
              .map((item) => item.pathway)
          )
        ).sort((a, b) => a.localeCompare(b))
      : [];

  const componentKey =
    subject && level
      ? subject === "History" && level === "HL" && pathway
        ? `${subject}_${pathway}`
        : subject === "History" && level === "HL"
        ? null
        : `${subject}_${level}`
      : null;

  const selectedPathwayNormalized =
    subject === "History" && level === "HL" && pathway
      ? normalizeHistoryPathway(pathway)
      : null;

  const subjectKeyCandidates =
    subject && level
      ? parsedSubjects
          .filter((item) => {
            if (item.subject !== subject || item.level !== level) return false;
            if (subject !== "History" || level !== "HL") return true;
            return selectedPathwayNormalized && item.pathway === selectedPathwayNormalized;
          })
          .map((item) => item.key)
      : [];

  const fallbackSubjectKeyCandidates =
    subject && level
      ? subject === "History" && level === "HL"
        ? selectedPathwayNormalized
          ? Array.from(new Set([
              `HL History ${selectedPathwayNormalized}`,
              `HL History ${selectedPathwayNormalized.replace(/\band\b/gi, "And")}`,
              `HL History ${selectedPathwayNormalized.replace(/\bAnd\b/gi, "and")}`,
            ]))
          : []
        : [`${level} ${subject}`]
      : [];

  const allSubjectKeyCandidates = Array.from(
    new Set([...subjectKeyCandidates, ...fallbackSubjectKeyCandidates])
  );

  const scoreFields = useMemo(
    () => (componentKey ? (COMPONENTS[componentKey] || []) : []),
    [componentKey]
  );

  const hasCompleteScores =
    !!componentKey &&
    scoreFields.length > 0 &&
    scoreFields.every(([k]) => scores[k] !== "");

  const boundaryOrder = getBoundaryResolutionOrder(boundary);

  let entry = null;
  let resolvedBoundary = null;
  if (hasCompleteScores) {
    for (const cb of boundaryOrder) {
      const cd = dataByBoundary[cb];
      if (!cd) continue;
      let candidateEntry = null;
      for (const sk of allSubjectKeyCandidates) {
        const found = getEntryForSubject(cd.timezones, sk, tz).entry;
        if (found) { candidateEntry = found; break; }
      }
      if (candidateEntry) { entry = candidateEntry; resolvedBoundary = cb; break; }
    }
  }
  const entryAvailable = Boolean(entry);

  useEffect(() => {
    if (isLoading) return;
    if (!hasCompleteScores) {
      if (boundaryNotFound) setBoundaryNotFound(false);
      return;
    }
    if (!entryAvailable) {
      setBoundaryNotFound((prev) => (prev ? prev : true));
    } else if (boundaryNotFound) {
      setBoundaryNotFound(false);
    }
  }, [isLoading, hasCompleteScores, entryAvailable, boundaryNotFound]);

  let grade = null;
  let neededText = null;
  let pointsAboveBoundary = null;
  if (hasCompleteScores && entry) {
    const total = scoreFields.reduce((sum, [k, , max, wt]) => {
      return sum + (Number(scores[k]) / max) * (parseFloat(wt) / 100) * 100;
    }, 0);
    const cutoffs = Array.from({ length: 7 }, (_, i) => parseFloat(entry[String(i + 1)]));
    for (let i = 6; i >= 0; i--) {
      if (total >= cutoffs[i]) {
        grade = i + 1;
        if (i < 6) {
          neededText = `Need ${(cutoffs[i + 1] - total).toFixed(1)} percentage points for grade ${i + 2}`;
        }
        break;
      }
    }

    if (grade !== null) {
      pointsAboveBoundary = getPointsAboveBoundary(total, entry, grade);
    }
  }

  useEffect(() => {
    if (!componentKey) return;

    setScores((currentScores) => {
      const nextScores = scoreFields.reduce(
        (acc, [k]) => ({ ...acc, [k]: currentScores[k] ?? "" }),
        {}
      );
      const keys = Object.keys(nextScores);
      const currentKeys = Object.keys(currentScores);
      const matchesCurrentScores =
        keys.length === currentKeys.length &&
        keys.every((key) => currentScores[key] === nextScores[key]);

      return matchesCurrentScores ? currentScores : nextScores;
    });
  }, [componentKey, scoreFields, setScores]);

  useEffect(() => {
    if (isLoading) return;

    const effectiveSubj =
      subject === "History" && level === "HL" && pathway
        ? `${subject} ${pathway}`
        : subject;
    if (grade !== null && effectiveSubj && level) {
      updateGrade(groupId, grade, effectiveSubj, level, neededText);
    } else {
      updateGrade(groupId, null, null, null);
    }
  }, [isLoading, grade, subject, level, pathway, neededText, updateGrade, groupId]);

  const fallbackBoundaryNote =
    resolvedBoundary && resolvedBoundary !== boundary
      ? `Result based on boundary "${resolvedBoundary}" because "${boundary}" is unavailable for this subject.`
      : null;

  if (isLoading) {
    return <div className="calculator-pad"><p>Loading…</p></div>;
  }
  if (Object.keys(dataByBoundary).length === 0) {
    return <div className="calculator-pad"><p>Boundary data unavailable.</p></div>;
  }

  return (
    <FlowSubjectRow groupId={groupId} fields={scoreFields} scores={scores} onScoresChange={setScores} grade={grade} neededText={neededText} pointsAboveBoundary={pointsAboveBoundary} fallbackBoundaryNote={fallbackBoundaryNote} error={boundaryNotFound ? "Boundary not available. Try another boundary (e.g., m24)." : null} levelControl={<FlowLevelSelect groupId={groupId} value={level} options={availableLevels} onChange={value => { setLevel(value); if (subject === "History" && value === "HL" && !pathway) setPathway("Europe"); setScores({}); }} />} choices={<>
      {headerExtras}{afterTitleExtras}
      <FlowChoice label="Subject"><select value={subject} onChange={e => { setSubject(e.target.value); setPathway(e.target.value === "History" ? "Europe" : ""); setScores({}); }}><option value="" disabled>Subject</option>{subjectOptions.map(value => <option key={value} value={value}>{value}</option>)}</select></FlowChoice>
      {subject === "History" && level === "HL" && <FlowChoice label="History region"><select value={pathway} onChange={e => { setPathway(e.target.value); setScores({}); }}><option value="" disabled>History region</option>{historyPathwayOptions.map(value => <option key={value} value={value}>{value}</option>)}</select></FlowChoice>}
    </>} />
  );
}

export default React.memo(GroupThree);
